import { Suspense, lazy, useMemo } from 'react';
import { Router, Route, Switch, Redirect, useLocation } from 'wouter';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import { SubscriptionProvider } from '@/contexts/subscription-context';
import { Toaster } from '@/components/ui/toaster';
import { FeedbackHost } from '@/features/feedback/FeedbackHost';
import { ScrollToTop } from '@/components/ScrollToTop';
import { showsCookieBanner } from '@/utils/cookieConsent';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { isPrerendering } from '@/marketing/plans';
import { isSubdomainSite } from '@/utils/subdomain';
import { ALL_NAMESPACES } from '@/i18n/localeSource';
import { initialNamespaces } from '@/i18n/namespaceGroups';
import i18n from './i18n/config';

// Pages
const HomePage = lazy(() => import('@/pages/home'));
// Where an app (Zapier, ChatGPT, Claude) sends the owner to approve access: behind the connected_apps flag, signs the owner in first (see features/connectedApps)
const ConsentPage = lazy(() => import('@/features/connectedApps/ConsentPage'));
// The unsubscribe link of the owner setup emails: no login, behind nothing the visitor can see (the server answers 404 while the flag is off)
// The public page Google Play links to (how to delete an account, what is kept): static, no sign-in, no API. Website only (stubbed in the app build).
const UnsubscribePage = lazy(() => import('@/features/activationEmails/UnsubscribePage'));
const LoginPage = lazy(() => import('@/pages/auth/login'));
const GoogleCallbackPage = lazy(() => import('@/pages/auth/google-callback'));
const RegisterOrganizationPage = lazy(() => import('@/pages/register'));

// Admin Pages
const AdminTeamPage = lazy(() => import('@/pages/admin/team'));
const AdminTeamDetailPage = lazy(() => import('@/pages/admin/team-detail'));
const BillingPage = lazy(() => import('@/pages/admin/billing'));
const AdminTipsPage = lazy(() => import('@/pages/admin/tips'));

// Quote-first owner screens (quotes, invoices, clients): one lazy chunk, none of it in the entry, booking or login bundles.
const OwnerArea = lazy(() => import('@/features/quotes/OwnerArea'));

// Crew: their own schedule, nothing else. Own lazy chunk, apart from the owner screens.
const CrewHome = lazy(() => import('@/features/crew/CrewHome'));
// Crew and estimators: More > Account (delete my login), behind the account_deletion flag; in the app and on the web
const CrewAccountPage = lazy(() => import('@/features/accountDeletion/AccountPage').then((m) => ({ default: m.CrewAccountPage })));
const CrewSettings = isNative() ? lazy(() => import('@/features/crew/CrewSettings')) : null;
const VisitRoute = lazy(() => import('@/features/crew/VisitRoute'));
const MyHoursPage = lazy(() => import('@/features/timesheets/MyHoursPage'));
const CrewLayout = lazy(() => import('@/features/crew/CrewLayout').then((m) => ({ default: m.CrewLayout })));

// Public quote and invoice pages (opened from a link): their own lazy chunk, rendered outside the auth, subscription and admin providers.
const ClientPages = lazy(() => import('@/features/clientPages/ClientPages'));

const StaffSettingsPage = lazy(() => import('@/pages/staff/settings'));



// Profile & Auth Pages
const ChangeEmailPage = lazy(() => import('@/pages/change-email'));
const ChangePasswordPage = lazy(() => import('@/pages/change-password'));
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'));
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'));
const AcceptInvitationPage = lazy(() => import('@/pages/accept-invitation'));
const ConfirmEmailPage = lazy(() => import('@/pages/confirm-email'));

// Pricing & Billing Pages
const PricingPage = lazy(() => import('@/pages/pricing'));

// Landing Pages
const AboutPage = lazy(() => import('@/pages/about'));
const IndustryPage = lazy(() => import('@/pages/industry'));
const IndustriesHub = lazy(() => import('@/pages/industries'));
const ComparisonRoute = lazy(() => import('@/pages/comparison'));

// Blog Pages
const BlogIndex = lazy(() => import('@/pages/blog'));
const BlogPostPage = lazy(() => import('@/pages/blog/BlogPost'));

// Legal Pages
const TemplateRoute = lazy(() => import('@/pages/template'));
// The noindex ad landing pages (/free/<template>): in the bundle only when the build switch is on (the literal check lets the build drop the chunk)
const FunnelRoute = import.meta.env.VITE_FUNNEL_PAGES === 'true' ? lazy(() => import('@/pages/funnel')) : null;
const GuideRoute = lazy(() => import('@/pages/guide'));
const TemplatesHub = lazy(() => import('@/pages/hub').then((m) => ({ default: m.TemplatesHub })));
const ResourcesHub = lazy(() => import('@/pages/hub').then((m) => ({ default: m.ResourcesHub })));
const ContactPage = lazy(() => import('@/pages/contact'));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/privacy'));
const TermsOfServicePage = lazy(() => import('@/pages/legal/terms'));
const CookiePolicyPage = lazy(() => import('@/pages/legal/cookies'));

// Cookie Consent (lazy-loaded to survive ad blockers blocking react-cookie-consent)
const CookieConsentBanner = lazy(() =>
  import('@/components/CookieConsentBanner')
    .then(m => ({ default: m.CookieConsentBanner }))
    .catch(() => ({ default: () => null }))
);

// The Meta Pixel's route tracker: mounted only in a web build with VITE_META_PIXEL_ID (the literal check drops this chunk otherwise; the app never has it).
// The library itself (src/lib/metaPixel.ts) ships inert in every web build, staging included, because the register page and the template form import it;
// without the id it never loads anything. With the id it loads Meta's script only with analytics consent, on allow-listed pages. A blocked chunk renders nothing.
const MetaPixelTracker = !isNative() && import.meta.env.VITE_META_PIXEL_ID
  ? lazy(() => import('@/components/MetaPixelTracker').catch(() => ({ default: () => null })))
  : null;

const UnauthorizedPage = lazy(() => import('@/pages/unauthorized'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));

// The app's route gate: a separate chunk that exists in the native build only (isNative() is a build-time constant).
const NativeGate = isNative() ? lazy(() => import('@/native/NativeGate').then((m) => ({ default: m.NativeGate }))) : null;

// Platform (super-admin) console
const PlatformRoutes = lazy(() => import('@/features/platform/PlatformRoutes'));

// Layouts
const MainLayout = lazy(() => import('@/layouts/main-layout').then(m => ({ default: m.MainLayout })));
const AdminLayout = lazy(() => import('@/layouts/admin-layout').then(m => ({ default: m.AdminLayout })));

// Protected Route Component
import { DefaultSEO } from '@/components/DefaultSEO';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { LoadingFallback } from '@/components/LoadingFallback';
import { GuestOnly } from '@/components/auth/guest-only';
import { ReadOnlyRefusalToast } from '@/components/ReadOnlyRefusalToast';
import { OWNER_AREA_PATH } from '@/features/quotes/owner-area-path';
import { OwnerRoute, ProfileLayoutWrapper, StaffSettingsRoute } from '@/routes/role-routes';
import { RETIRED_MARKETING_PATHS } from '@/config/retired-marketing-paths';
import { isClientPagePath } from '@/features/clientPages/lib/route';
import { isNative } from '@/platform/native';
import { isUnsubscribePath } from '@/features/activationEmails/paths';
import { AuthGate } from '@/features/authGate/AuthGate';

// Suspense boundary for lazily loaded pages. Used inside layouts so the shell
// (sidebar, top bar) stays on screen while a page chunk downloads.
function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
}

// The first page only preloads the translation namespaces it needs (see i18n/namespaceGroups.ts).
// After a client-side navigation to a page that needs more (login -> admin, home -> pricing, ...),
// wait for that page's namespaces before rendering it, exactly like a cold load of that URL
// would, so no component ever shows a raw translation key. On a cold load everything is already
// loaded, so this renders straight through (no extra Suspense round, which in React 18 can add
// up to 500 ms).
// Outside the Router, so it reads the browser location: the console has no cookie banner (it would cover its screens).
function CookieBannerGate() {
  const [pathname] = useLocation();
  // the build-time snapshot of a marketing page has no cookie banner (it is the same for every visitor)
  return showsCookieBanner(pathname) && !isPrerendering() ? <Suspense fallback={null}><CookieConsentBanner /></Suspense> : null;
}

function NamespaceGate({ children }: { children: React.ReactNode }) {
  const [pathname] = useLocation();
  const namespaces = useMemo(
    () => initialNamespaces(ALL_NAMESPACES, pathname, isSubdomainSite()),
    [pathname],
  );
  if (i18n.isInitialized && !namespaces.every((ns) => i18n.hasLoadedNamespace(ns))) {
    throw i18n.loadNamespaces(namespaces);
  }
  return <>{children}</>;
}

const APP_VERSION = __APP_VERSION__;

// Native: only the sign-in, admin, staff and crew routes exist (see src/native/NativeGate.tsx). Web: renders the routes as they are.
function RouteGate({ children }: { children: React.ReactNode }) {
  return NativeGate ? <NativeGate>{children}</NativeGate> : <>{children}</>;
}

function App() {
  // /q/:token and /i/:token: no login, no providers, no cookie banner, nothing of the owner app.
  // Not in the app: NativeGate sends those paths to /login.
  if (!isNative() && isClientPagePath(window.location.pathname)) {
    return (
      <Suspense fallback={null}>
        <ClientPages />
      </Suspense>
    );
  }
  // /u/<token>, the unsubscribe link of the setup emails: the same lightweight, no-session surface (own query client, no cookie banner, no canonical)
  if (isUnsubscribePath(window.location.pathname)) {
    return (
      <Suspense fallback={null}>
        <UnsubscribePage />
      </Suspense>
    );
  }
  return (
    <HelmetProvider>
      <Suspense fallback={<LoadingFallback />}>
        <AuthProvider>
          <SubscriptionProvider>
            <Router>
              {/* ONE loading screen from the moment auth work starts until the destination is ready (the app stays mounted, hidden, behind it) */}
              <AuthGate>
              {!isNative() && <DefaultSEO />}
              <ScrollToTop />
              {MetaPixelTracker && <Suspense fallback={null}><MetaPixelTracker /></Suspense>}
              {/* Global impersonation banner (shows across all layouts) */}
              <ImpersonationBanner />
              {/* Any write refused because the account is read-only is explained here, on every screen */}
              <ReadOnlyRefusalToast />
              {/* Send feedback: the sheet, and the outbox of notes written offline (behind the `feedback` flag, signed-in team members only) */}
              <FeedbackHost />
              {/* Hidden version indicator for deployment verification */}
              <p style={{ display: 'none' }} data-app-version={APP_VERSION}>v{APP_VERSION}</p>
              <Page>
              <NamespaceGate>
              <RouteGate>
              <Switch>
                {/* Public routes */}
                <Route path="/">
                  {() => {
                    return <HomePage />;
                  }}
                </Route>
                <Route path="/login">{() => <GuestOnly><LoginPage /></GuestOnly>}</Route>
                <Route path="/register">{() => <GuestOnly><RegisterOrganizationPage /></GuestOnly>}</Route>

                {/* Platform console (separate PlatformAdmin identity): src/features/platform/PlatformRoutes.tsx */}
                <Route path="/platform" component={PlatformRoutes} />
                <Route path="/platform/*" component={PlatformRoutes} />
                <Route path="/oauth/consent" component={ConsentPage} />
                <Route path="/auth/google/callback" component={GoogleCallbackPage} />
                <Route path="/unauthorized" component={UnauthorizedPage} />

                {/* Public pricing page */}
                <Route path="/pricing" component={PricingPage} />

                {/* Retired booking-era marketing pages (industry landing pages, comparisons, features tour): old links go home */}
                {RETIRED_MARKETING_PATHS.map((path) => (
                  <Route key={path} path={path}>{() => <Redirect to="/" replace />}</Route>
                ))}
                <Route path="/about" component={AboutPage} />
                <Route path="/for" component={IndustriesHub} />
                <Route path="/compare/:slug" component={ComparisonRoute} />
                <Route path="/alternatives/:slug" component={ComparisonRoute} />
                <Route path="/for/:slug" component={IndustryPage} />
                <Route path="/contact" component={ContactPage} />
                <Route path="/templates" component={TemplatesHub} />
                <Route path="/templates/:slug" component={TemplateRoute} />
                {FunnelRoute && <Route path="/free/:slug" component={FunnelRoute} />}
                <Route path="/resources" component={ResourcesHub} />
                <Route path="/resources/:slug" component={GuideRoute} />

                {/* Blog */}
                <Route path="/blog" component={BlogIndex} />
                <Route path="/blog/:slug" component={BlogPostPage} />

                {/* Legal pages */}
                <Route path="/privacy" component={PrivacyPolicyPage} />
                <Route path="/terms" component={TermsOfServicePage} />
                <Route path="/cookies" component={CookiePolicyPage} />
                <Route path="/legal/privacy" component={PrivacyPolicyPage} />
                <Route path="/legal/terms" component={TermsOfServicePage} />
                <Route path="/legal/cookies" component={CookiePolicyPage} />

                {/* Public auth pages */}
                <Route path="/forgot-password" component={ForgotPasswordPage} />
                <Route path="/reset-password/:token" component={ResetPasswordPage} />
                <Route path="/accept-invitation/:token" component={AcceptInvitationPage} />
                <Route path="/confirm-email/:userId/:token" component={ConfirmEmailPage} />

                {/* The app's crew settings (phones, privacy, terms, account deletion); no such route on the website. */}
                {CrewSettings && (
                  <Route path="/crew/settings">
                    <ProtectedRoute requiredRole="crew">
                      <Page>
                        <CrewLayout><CrewSettings /></CrewLayout>
                      </Page>
                    </ProtectedRoute>
                  </Route>
                )}

                {/* Crew account page (delete my login): the flag-off answer sends crew home */}
                <Route path="/crew/account">
                  <ProtectedRoute requiredRole="crew">
                    <Page>
                      <CrewLayout><CrewAccountPage /></CrewLayout>
                    </Page>
                  </ProtectedRoute>
                </Route>

                {/* Crew landing: crew, plus admins and staff (J5). Any /admin or /staff path sends crew here. */}
                <Route path="/crew">
                  <ProtectedRoute requiredRole="crew">
                    <Page>
                      <CrewLayout><CrewHome /></CrewLayout>
                    </Page>
                  </ProtectedRoute>
                </Route>

                <Route path="/crew/hours">
                  <ProtectedRoute requiredRole="crew">
                    <Page>
                      <CrewLayout><MyHoursPage /></CrewLayout>
                    </Page>
                  </ProtectedRoute>
                </Route>

                <Route path="/crew/visits/:id">
                  <ProtectedRoute requiredRole="crew">
                    <Page>
                      <CrewLayout><VisitRoute /></CrewLayout>
                    </Page>
                  </ProtectedRoute>
                </Route>

                {/* Profile tab of Settings: also for estimators (the sidebar profile link); must be BEFORE /staff/:rest* (staff only). */}
                <Route path="/staff/settings">
                  <StaffSettingsRoute><StaffSettingsPage /></StaffSettingsRoute>
                </Route>

                {/* Staff routes */}
                <Route path="/staff/:rest*">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <Page>
                      <Switch>
                        {/* old bookmarks of the booking-era staff pages */}
                        <Route path="/staff/:rest*" component={() => <Redirect to="/admin/quotes" replace />} />
                      </Switch>
                      </Page>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Legacy staff route - redirect to the quotes list */}
                <Route path="/dashboard">
                  <ProtectedRoute requiredRole="staff">
                    <Redirect to="/admin/quotes" />
                  </ProtectedRoute>
                </Route>

                {/* Quote-first screens - admins + staff (decision 6). Must be BEFORE /admin/:rest*. */}
                <Route path={OWNER_AREA_PATH}>
                  <OwnerRoute><OwnerArea /></OwnerRoute>
                </Route>

                {/* Team detail - admin only. Must be BEFORE /admin/:rest* (which routes /admin/team to the index page). */}
                <Route path="/admin/team/:id">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <Page><AdminTeamDetailPage /></Page>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Bare /admin: `/admin/:rest*` needs a segment, so this used to be a 404 */}
                <Route path="/admin">{() => <Redirect to="/admin/dashboard" replace />}</Route>

                {/* Admin routes */}
                <Route path="/admin/:rest*">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <Page>
                      <Switch>
                        <Route path="/admin/team" component={AdminTeamPage} />
                        <Route path="/admin/billing" component={BillingPage} />
                        <Route path="/admin/tips" component={AdminTipsPage} />
                        {/* old bookmarks of the booking-era admin pages */}
                        <Route path="/admin/:rest*" component={() => <Redirect to="/admin/dashboard" replace />} />
                      </Switch>
                      </Page>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Email change (all authenticated users) - uses AdminLayout for admin/staff */}
                <Route path="/change-email">
                  <ProtectedRoute>
                    <ProfileLayoutWrapper>
                      <Page><Route path="/change-email" component={ChangeEmailPage} /></Page>
                    </ProfileLayoutWrapper>
                  </ProtectedRoute>
                </Route>

                {/* Password change (all authenticated users) - uses AdminLayout for admin/staff */}
                <Route path="/change-password">
                  <ProtectedRoute>
                    <ProfileLayoutWrapper>
                      <Page><Route path="/change-password" component={ChangePasswordPage} /></Page>
                    </ProfileLayoutWrapper>
                  </ProtectedRoute>
                </Route>

                {/* 404 */}
                <Route component={NotFoundPage} />
              </Switch>
              </RouteGate>
              </NamespaceGate>
              </Page>
            </AuthGate>
            </Router>
            <Toaster />
            {!isNative() && <CookieBannerGate />}
          </SubscriptionProvider>
        </AuthProvider>
      </Suspense>
    </HelmetProvider>
  );
}

export default App;
